import React from 'react';
import ArticleShell from './ArticleShell';
import { H2, H3, P, Callout, BulletList } from './academyPrimitives';
import { getGuideBySlug } from '../../data/knowledgeAcademy';

const UAECorporateTaxGuide = () => {
  const guide = { ...getGuideBySlug('uae-corporate-tax-guide-2026'), relatedService: { label: 'Corporate Tax Services', path: '/corporate-tax-uae' } };

  const toc = [
    { id: 'introduction', label: 'Introduction' },
    { id: 'what-is-ct', label: 'What is UAE Corporate Tax?' },
    { id: 'rates', label: 'Corporate Tax Rates' },
    { id: 'who-must-register', label: 'Who Must Register?' },
    { id: 'taxable-income', label: 'Taxable Income' },
    { id: 'deductible-expenses', label: 'Deductible Expenses' },
    { id: 'small-business-relief', label: 'Small Business Relief' },
    { id: 'free-zone-ct', label: 'Free Zone Corporate Tax' },
    { id: 'transfer-pricing', label: 'Transfer Pricing' },
    { id: 'returns', label: 'Corporate Tax Returns' },
    { id: 'mistakes', label: 'Common Mistakes' },
    { id: 'penalties', label: 'Penalties' },
    { id: 'wpaa-help', label: 'How WPAA Can Help' }
  ];

  return (
    <ArticleShell guide={guide} toc={toc} testId="ka-article-ct" ctaService="corporate-tax">
      <Callout variant="info" title="A note on this guide">
        UAE Corporate Tax rules continue to evolve through Cabinet Decisions and FTA guidance. This guide covers the enduring fundamentals. For anything specific to your business, or for the latest FTA rules, please book a free consultation.
      </Callout>

      <H2 id="introduction">Introduction</H2>
      <P>The UAE introduced Corporate Tax to align with international tax standards while maintaining a competitive business environment. Corporate Tax generally applies from financial years beginning on or after <strong>1 June 2023</strong>.</P>

      <H2 id="what-is-ct">What is UAE Corporate Tax?</H2>
      <P>Corporate Tax is a <strong>direct tax</strong> imposed on the taxable income of businesses.</P>

      <H2 id="rates">Corporate Tax Rates</H2>
      <div className="grid md:grid-cols-2 gap-4 my-6">
        <div className="border-2 border-emerald-100 bg-emerald-50/50 rounded-xl p-5">
          <p className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-2">Up to AED 375,000</p>
          <p className="text-3xl font-bold text-gray-900 mb-2">0%</p>
          <p className="text-sm text-gray-700">Taxable income up to AED 375,000 is taxed at 0%.</p>
        </div>
        <div className="border-2 border-teal-100 bg-teal-50/50 rounded-xl p-5">
          <p className="text-xs font-bold uppercase tracking-wider text-teal-700 mb-2">Above AED 375,000</p>
          <p className="text-3xl font-bold text-gray-900 mb-2">9%</p>
          <p className="text-sm text-gray-700">Taxable income exceeding AED 375,000 is taxed at 9%.</p>
        </div>
      </div>
      <P>Certain multinational groups may be subject to additional rules.</P>

      <H2 id="who-must-register">Who Must Register?</H2>
      <p className="text-gray-700 mb-2">Corporate Tax registration is mandatory for:</p>
      <BulletList items={['Mainland companies', 'Free Zone companies', 'Foreign entities with UAE nexus', 'Certain individuals conducting business activities']} />

      <H2 id="taxable-income">Taxable Income</H2>
      <P>Corporate Tax is based on <strong>accounting profit adjusted for tax purposes</strong>.</P>
      <p className="text-gray-700 mb-2">Adjustments may include:</p>
      <BulletList items={['Non-deductible expenses', 'Related-party transactions', 'Exempt income', 'Tax relief provisions']} />

      <H2 id="deductible-expenses">Deductible Expenses</H2>
      <H3>Generally deductible</H3>
      <BulletList items={['Salaries', 'Rent', 'Utilities', 'Professional fees', 'Business travel']} />
      <H3>Potentially restricted</H3>
      <BulletList items={['Entertainment expenses', 'Certain interest expenses', 'Non-business expenses']} />

      <H2 id="small-business-relief">Small Business Relief</H2>
      <P>Businesses with qualifying revenue may elect <strong>Small Business Relief</strong>, subject to conditions. This is often relevant for SMEs and start-ups.</P>

      <H2 id="free-zone-ct">Free Zone Corporate Tax</H2>
      <P>Free Zone entities may qualify for the <strong>0% Corporate Tax rate on qualifying income</strong> if they meet specific requirements (substance, qualifying activities, audited financials, transfer pricing, de minimis).</P>
      <Callout variant="warning" title="Compliance is not automatic">
        Free Zone status alone does not guarantee 0% Corporate Tax. Failure to meet the Qualifying Free Zone Person requirements may result in loss of the benefit and taxation at 9%.
      </Callout>

      <H2 id="transfer-pricing">Transfer Pricing</H2>
      <P>Businesses must ensure related-party transactions are conducted at <strong>arm&apos;s length</strong>. Transfer Pricing documentation (disclosure form, Local File, Master File) may be required depending on group and revenue thresholds.</P>

      <H2 id="returns">Corporate Tax Returns</H2>
      <p className="text-gray-700 mb-2">Businesses must:</p>
      <BulletList items={['Register with the FTA', 'Maintain proper accounting records', 'Prepare financial statements', 'File Corporate Tax returns on time']} />

      <H2 id="mistakes">Common Corporate Tax Mistakes</H2>
      <BulletList items={['Missing registration deadlines', 'Ignoring transfer pricing obligations', 'Incorrect expense treatment', 'Poor bookkeeping', 'Assuming free zones are automatically exempt']} />

      <H2 id="penalties">Penalties</H2>
      <p className="text-gray-700 mb-2">Penalties may apply for:</p>
      <BulletList items={['Failure to register', 'Failure to file', 'Incorrect disclosures', 'Record-keeping failures']} />

      <H2 id="wpaa-help">How WPAA Can Help</H2>
      <P>Corporate Tax compliance begins with proper accounting records. Early planning helps businesses minimise risk and stay compliant with UAE regulations.</P>
      <P>WPAA provides Corporate Tax registration, computation, filing and advisory services, including Qualifying Free Zone Person assessments and transfer pricing support.</P>
    </ArticleShell>
  );
};

export default UAECorporateTaxGuide;
